import {
  FetchProductPickerProductsQueryVariables,
  InputMaybe,
} from "@src/generated";
import store from "@src/store";
import {
  setProductPickerProductCount,
  setProductPickerProducts,
} from "@src/store/slices/productPicker";
import {
  ProductPickerFilterType,
  ProductPickerProduct,
} from "@src/store/slices/productPicker/types";
import { loadProductCount } from "@src/store/slices/productPicker/util/loadProductCount";
import { loadProducts } from "@src/store/slices/productPicker/util/loadProducts";
import { AgGridCursorDataSource } from "@src/utils/AgGridCursorDataSource";

export class ProductPickerProductDataSource extends AgGridCursorDataSource<ProductPickerProduct> {
  private filters: Record<ProductPickerFilterType, string[]> = {
    product_type: [],
    vendor: [],
    tag: [],
    status: [],
    collection_id: [],
  };
  private search: string = "";
  private query: InputMaybe<string> | undefined = undefined;
  private allowIgProducts: boolean = false;

  constructor(allowIgProducts: boolean) {
    super();
    this.allowIgProducts = allowIgProducts;
  }

  public setFilters(filters: Record<ProductPickerFilterType, string[]>) {
    if (JSON.stringify(filters) !== JSON.stringify(this.filters)) {
      this.filters = filters;
      this.reload();
    }
  }

  public setSearch(search: string) {
    if (search != this.search) {
      this.search = search;
      this.debouncedReload();
    }
  }

  public clear() {
    super.clear();
    this.filters = {
      product_type: [],
      vendor: [],
      tag: [],
      status: [],
      collection_id: [],
    };
    this.search = "";
    this.query = undefined;
  }

  protected async fetchData() {
    const variables = this.createVariables();
    if (variables.query !== this.query) {
      store.dispatch(setProductPickerProductCount(undefined));
      this.query = variables.query;
      void loadProductCount({ query: this.query }).then((count) => {
        store.dispatch(setProductPickerProductCount(count));
      });
    }
    const result = await loadProducts(variables);
    this.data = result.data;
    this.endCursor = result.endCursor;
    this.hasNextPage = result.hasNextPage;
  }

  protected createVariables(): FetchProductPickerProductsQueryVariables {
    const queries: string[] = [];

    // Filter out with the "price_test" tag since shopify collection id gql query is incompatible w/ tags query
    if (
      !this.filters?.collection_id ||
      (this.filters.collection_id.length === 0 && !this.allowIgProducts)
    ) {
      queries.push("-price_test");
    }

    if (this.search) {
      queries.push(this.search);
    }

    if (this.filters) {
      for (const [name, value] of Object.entries(this.filters)) {
        if (value && value.length > 0) {
          // Check if array exists and has items
          if (name === "collection_id") {
            // Collection ID doesn't need quotes (numeric field)
            value.forEach((id) => {
              queries.push(`${name}:${id}`);
            });
          } else if (name === "product_type") {
            // Use quotes for text fields to ensure exact match
            value.forEach((type) => {
              queries.push(`${name}:"${type}"`);
            });
          } else if (name === "tag") {
            // Handle multiple tags with AND logic - each tag as separate condition
            value.forEach((tag) => {
              queries.push(`${name}:"${tag}"`);
            });
          } else if (name === "vendor") {
            // Handle multiple vendors with AND logic - each vendor as separate condition
            value.forEach((vendor) => {
              queries.push(`${name}:"${vendor}"`);
            });
          } else if (name === "status") {
            // Handle multiple status values with AND logic
            value.forEach((status) => {
              queries.push(`${name}:"${status}"`);
            });
          } else {
            // Generic handler for other array-based filters
            value.forEach((item) => {
              // Use quotes for text fields, no quotes for numeric fields
              if (typeof item === "string" && isNaN(Number(item))) {
                queries.push(`${name}:"${item}"`);
              } else {
                queries.push(`${name}:${item}`);
              }
            });
          }
        }
      }
    }

    return {
      first: 250,
      after: this.endCursor,
      query: queries.join(" "), // Space-separated for implicit AND in Shopify
    };
  }

  protected onDataUpdate() {
    store.dispatch(setProductPickerProducts(this.data));
  }
}
